<script setup>
import {computed, ref, watch} from "vue";
import {oneOfNumberValidator, requiredMessage, stringValidator} from "@/utils/yupValidators";
import * as yup from "yup";
import {useMutation} from "@vue/apollo-composable";
import {HpoRole, HpoRoleLabel} from "../../choices";
import {HpoSaveMemberMutation} from "@/graphql/mutations/hpo/member.graphql"
import Modal from "@/components/modal/Modal.vue";
import {useDjangoForm} from "../../../../utils/forms";

const props = defineProps({
  hpoId: {
    type: String,
    required: true
  },
  member: {
    type: Object,
    required: true
  },
  editedMember: {
    type: Object,
    required: false,
    default: {}
  },
  memberTypeOptions: {
    type: Array,
    required: true
  },
  hospitalList: {
    type: Array,
    required: false,
    default: []
  },
})

const emit = defineEmits(['close'])

function showHospitalCheckboxes(role) {
  return [HpoRole.CONTACT_POINT, HpoRole.ER, HpoRole.HOSPITAL_SCREEN].includes(parseInt(role))
}


const {defineField, handleMutationSubmit, nonFieldErrors} = useDjangoForm({
  validationSchema: yup.object({
    role: oneOfNumberValidator(HpoRole),
    hpoHospitals: yup.array().when(
      'role', {
        is: (val) => showHospitalCheckboxes(val),
        then: () => {
          return yup.array().of(yup.string().oneOf(Object.keys(hospitalOptions.value)))
            .min(1, gettext('At least one hospital must be selected'))
            .max(Object.values(hospitalOptions.value).length)
        },
        otherwise: () => {
          return yup.array().of(yup.number().integer()).max(0, gettext('No hospital can be selected'))
        }
      }
    ),
    name: stringValidator.required(requiredMessage),
  }),
  initialValues: {
    id: props.editedMember?.id || null,
    name: props.editedMember?.name || null,
    role: props.editedMember?.role || null,
    hpoHospitals: props.editedMember?.hpoHospitals?.map(a => parseInt(a.id)) ?? []
  }
});

const [id, idProps] = defineField('id');
const [name, nameProps] = defineField('name');
const [role, roleProps] = defineField('role');
const [hpoHospitals, hpoHospitalsProps] = defineField('hpoHospitals');

// Plaintext PIN returned once when a new anonymous member is created; show it to
// the manager to hand out, then close on confirm. Null on edit -> close directly.
const createdPin = ref(null)

const onSubmit = handleMutationSubmit(
  (values) => {
    return mutate({...values, hpo: props.hpoId})
  },
  {
    onSuccess(result) {
      if (result?.pin) {
        createdPin.value = result.pin;
      } else {
        close();
      }
    },
  }
);

watch(role, async (newVal, oldVal) => {
  if (parseInt(newVal) !== HpoRole.CONTACT_POINT) {
    hpoHospitals.value = []
  }
})


const hospitalOptions = computed(() => {
  return props.hospitalList.reduce((obj, item) => Object.assign(obj, {[item.id]: item.name}), {});
})


const {mutate} = useMutation(HpoSaveMemberMutation)

const roleOptions = computed(() => {
  return Object.entries(HpoRoleLabel)
    .map(([key, value]) => ({value: Number(key), title: value}))  // Convert to array of objects
    .sort((a, b) => a.title.localeCompare(b.title, 'cs'));           // Sort by label (Czech locale)
})

function close() {
  emit('close')
}

</script>

<template>
  <Modal :title="pgettext('action', 'Edit team member')" :max-width="500" @close="close" :closeOnClickOutside="false">
    <div class="hpo-member-form">
      <template v-if="createdPin">
        <v-alert type="success" class="mb-4">
          <div>{{ pgettext('label', 'Member created. Give them this PIN to log in — it is shown only once:') }}</div>
          <h2 class="mt-2">{{ createdPin }}</h2>
        </v-alert>
        <v-btn block color="primary" :text="pgettext('action', 'Done')" @click="close"/>
      </template>
      <v-form v-else @submit.prevent="onSubmit">
        <v-row>
          <v-col cols="12">
            <v-alert v-for="message in nonFieldErrors" :title="message" type="error"></v-alert>
            <v-text-field v-model="name"
                          v-bind="nameProps"
                          autocomplete="off"
                          :label="pgettext('label', 'Name')"/>

            <v-select v-model="role"
                      v-bind="roleProps"
                      :label="pgettext('label', 'Role')"
                      :items="roleOptions"
            />


            <template v-if="showHospitalCheckboxes(role)">
              <v-checkbox
                v-for="([value, label], index) in Object.entries(hospitalOptions)"
                :key="index"
                v-model="hpoHospitals"
                v-bind="hpoHospitalsProps"
                :label="label"
                :value="Number(value)"
                color="primary"
                density="compact"
              ></v-checkbox>
            </template>
            <v-btn block type="submit" :text="pgettext('action', 'Save')"/>
          </v-col>
        </v-row>
      </v-form>
    </div>
  </Modal>
</template>